import { CONSTANTS } from '../game.js';
import { EventTypes } from '../events/EventTypes.js';

export class TimerManager {
    /**
     * タイマーマネージャーのコンストラクタ
     * @param {Phaser.Scene} scene - Phaserのシーンオブジェクト
     */
    constructor(scene) {
        this.scene = scene;
        
        // タイマー関連の初期化
        this.remainingTime = CONSTANTS.TIMER.INITIAL_TIME;
        this.elapsedTime = 0; // 実際の経過時間を追跡する変数を追加
        this.isTimerRunning = false;
        this.timerEvent = null;
        
        // タイマー表示用のテキストオブジェクト
        this.timerText = null;
        
        // タイマー速度の倍率（デフォルトは1倍速）
        this.speedMultiplier = 1;
    }

    /**
     * タイマーを開始する
     */
    startTimer() {
        if (!this.isTimerRunning) {
            this.isTimerRunning = true;
            this.remainingTime = CONSTANTS.TIMER.INITIAL_TIME;
            this.elapsedTime = 0; // 経過時間をリセット
            
            // タイマーイベントを設定
            this.timerEvent = this.scene.time.addEvent({
                delay: CONSTANTS.TIMER.UPDATE_INTERVAL,
                callback: this.updateTimer,
                callbackScope: this,
                loop: true
            });
        }
    }

    /**
     * タイマーを停止する
     */
    stopTimer() {
        if (this.timerEvent) {
            this.timerEvent.destroy();
            this.timerEvent = null;
        }
        this.isTimerRunning = false;
    }

    /**
     * タイマーを更新する
     */
    updateTimer() {
        if (this.isTimerRunning) {
            // 実際の更新間隔を計算（速度倍率を考慮）
            const updateInterval = (CONSTANTS.TIMER.UPDATE_INTERVAL / 1000) * this.speedMultiplier;
            
            // 経過時間を累積
            this.elapsedTime += updateInterval;
            
            // 残り時間を減らす
            this.remainingTime -= updateInterval;
            
            // タイマー表示の更新
            this.updateTimerDisplay();
            
            // 時間切れの場合
            if (this.remainingTime <= 0) {
                this.remainingTime = 0;
                this.scene.events.emit(EventTypes.TIMER.TIMEUP);
            }
        }
    }

    /**
     * 時間を延長する
     * @param {number} seconds - 延長する秒数
     */
    extendTime(seconds) {
        if (this.isTimerRunning) {
            this.remainingTime += seconds;
            this.updateTimerDisplay();
        }
    }
    
    /**
     * 時間を短縮する
     * @param {number} seconds - 短縮する秒数
     */
    reduceTime(seconds) {
        if (this.isTimerRunning) {
            this.remainingTime -= seconds;
            // 残り時間が0未満にならないようにする
            if (this.remainingTime < 0) {
                this.remainingTime = 0;
            }
            this.updateTimerDisplay();
        }
    }
    
    /**
     * タイマーの速度を設定する
     * @param {number} multiplier - 速度の倍率（1が通常速度）
     */
    setTimerSpeed(multiplier) {
        this.speedMultiplier = multiplier;
    }
    
    /**
     * 現在のタイマー速度を取得する
     * @returns {number} 速度の倍率
     */
    getTimerSpeed() {
        return this.speedMultiplier;
    }

    /**
     * 残り時間を取得する
     * @returns {number} 残り時間（秒）
     */
    getRemainingTime() {
        return Math.max(0, this.remainingTime);
    }
    
    /**
     * 経過時間を取得する
     * @returns {number} 経過時間（秒）
     */
    getElapsedTime() {
        return this.elapsedTime;
    }

    /**
     * 時間切れかどうかを判定する
     * @returns {boolean} 時間切れの場合true
     */
    isTimeUp() {
        return this.remainingTime <= 0;
    }

    /**
     * タイマー表示を作成する
     */
    createTimerDisplay() {
        this.timerText = this.scene.add.text(
            this.scene.game.config.width - 20,
            20,
            this.formatTime(CONSTANTS.TIMER.INITIAL_TIME),
            {
                fontSize: '32px',
                fontFamily: 'DotGothic16',
                fill: '#ffffff',
                stroke: '#000000',
                strokeThickness: 4
            }
        );
        this.timerText.setOrigin(1, 0);  // 右上を基準点に
        this.timerText.setDepth(20);     // 他のUI要素より手前に
    }

    /**
     * タイマー表示を更新する
     */
    updateTimerDisplay() {
        if (this.timerText) {
            this.timerText.setText(this.formatTime(this.getRemainingTime()));
            
            // 残り時間に応じて色を変更
            if (this.remainingTime <= 10) {
                this.timerText.setStyle({ fill: '#ff0000' });  // 残り10秒以下で赤
            } else if (this.remainingTime <= 30) {
                this.timerText.setStyle({ fill: '#ffff00' });  // 残り30秒以下で黄
            } else {
                this.timerText.setStyle({ fill: '#ffffff' });  // 通常時は白
            }
        }
    }

    /**
     * 時間を表示用の文字列にフォーマットする
     * @param {number} time - 時間（秒）
     * @returns {string} フォーマットされた時間文字列
     */
    formatTime(time) {
        return Math.max(0, time).toFixed(1);
    }

    /**
     * 時間切れ時の視覚的エフェクトを表示
     */
    showTimeUpEffect() {
        if (this.timerText) {
            // タイマーテキストを点滅
            this.scene.tweens.add({
                targets: this.timerText,
                alpha: { from: 1, to: 0 },
                duration: 100,
                yoyo: true,
                repeat: 5
            });
        }
        
        // 画面中央に大きく「Time Up !」と表示
        const timeupText = this.scene.add.text(
            this.scene.game.config.width / 2,
            this.scene.game.config.height / 2,
            'Time Up!',
            {
                fontSize: '96px',
                fontFamily: 'DotGothic16', // Dot風フォントを指定
                fontStyle: 'bold',
                fill: '#ff0000',
                stroke: '#ffffff',
                strokeThickness: 8,
                align: 'center'
            }
        );
        timeupText.setOrigin(0.5);
        timeupText.setDepth(30); // 最前面に表示
        
        // アニメーション効果（拡大→縮小）
        this.scene.tweens.add({
            targets: timeupText,
            scale: { from: 0.5, to: 1.0 },
            duration: 500,
            ease: 'Bounce.Out',
            onComplete: () => {
                // 少し縮小して安定させる
                this.scene.tweens.add({
                    targets: timeupText,
                    scale: 1.0,
                    duration: 200,
                    ease: 'Power2.Out'
                });
            }
        });
        
        // GameOverSceneに遷移する前にテキストを自動的に破棄するためのタイマー
        this.scene.time.delayedCall(900, () => {
            if (timeupText) {
                timeupText.destroy();
            }
        });
    }

    /**
     * リソースのクリーンアップとリセット
     */
    cleanup() {
        this.stopTimer();
        this.remainingTime = CONSTANTS.TIMER.INITIAL_TIME;
        this.elapsedTime = 0;
        
        if (this.timerText) {
            this.timerText.destroy();
            this.timerText = null;
        }
    }
}
